<script setup lang="ts">
import { computed, ref, watch } from 'vue'
import { debouncer } from '@/util/debouncer'
import { ALGOLIA_BYTE_LIMIT, isQueryTooLong } from '@/util/browse'

const props = withDefaults(
  defineProps<{
    query?: string
    labelText?: string
    hideLabel?: boolean
    placeholderText?: string
    buttonText?: string
    debounceDurationMs?: number
    hideButton?: boolean
    inputId?: string
    byteLimitEnforced?: boolean
    byteLimit?: number
    byteLimitMessage?: string
    isSearching?: boolean
    isDisabled?: boolean
  }>(),
  {
    query: '',
    labelText: 'Search',
    hideLabel: false,
    placeholderText: 'Keyword search',
    buttonText: 'Search',
    debounceDurationMs: 200,
    hideButton: false,
    inputId: 'search-input',
    byteLimitEnforced: false,
    byteLimit: ALGOLIA_BYTE_LIMIT,
    byteLimitMessage: 'Please use a shorter search query.',
    isSearching: false,
    isDisabled: false
  }
)

// query:typed reports every keystroke; query:changed is the debounced query to search for.
const emit = defineEmits<{ 'query:changed': [query: string], 'query:typed': [query: string] }>()

const searchQuery = ref(props.query)

const warningId = computed(() => `${props.inputId}-warning`)
const byteLimitReached = computed(
  () => props.byteLimitEnforced && isQueryTooLong(searchQuery.value, props.byteLimit)
)

let lastEmittedQuery = props.query

function emitCurrentQuery() {
  lastEmittedQuery = searchQuery.value
  emit('query:changed', searchQuery.value)
}

const emitQuery = debouncer(emitCurrentQuery, props.debounceDurationMs)

function handleTyping() {
  emit('query:typed', searchQuery.value)
  emitQuery()
}

function submitQuery() {
  emitQuery.cancel()
  emitCurrentQuery()
}

// The page's query also changes from outside (a removed chip, back and forward). Syncing
// to an echo of our own emit would overwrite whatever was typed since.
watch(
  () => props.query,
  (pageQuery) => {
    const isEchoOfOwnEmit = pageQuery === lastEmittedQuery
    if (isEchoOfOwnEmit) {
      return
    }
    searchQuery.value = pageQuery
    lastEmittedQuery = pageQuery
  }
)
</script>

<template>
  <div>
    <label :for="inputId" class="mb-1 block text-text-sm font-bold text-gray-700" :class="{ 'sr-only': hideLabel }">
      {{ labelText }}
    </label>
    <div class="flex">
      <input
        :id="inputId"
        v-model="searchQuery"
        type="search"
        class="w-full rounded-xs border border-gray-700 px-3 py-1.5 text-text-md text-gray-900 placeholder:text-gray-700 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-blue-600"
        :class="{ 'rounded-r-none': !hideButton }"
        :placeholder="placeholderText"
        :aria-describedby="byteLimitReached ? warningId : undefined"
        :aria-invalid="byteLimitReached"
        :aria-busy="isSearching"
        :disabled="isDisabled"
        @input="handleTyping"
      />
      <slot v-if="!hideButton" name="button">
        <button
          type="button"
          class="rounded-xs rounded-l-none bg-blue-400 px-3 py-1.5 text-text-lg text-white disabled:opacity-50"
          :disabled="isDisabled || !searchQuery"
          @click="submitQuery()"
        >
          <span class="material-icons-round align-middle text-text-lg" aria-hidden="true">search</span>
          <span class="sr-only sm:not-sr-only sm:ml-1">{{ buttonText }}</span>
        </button>
      </slot>
    </div>
    <p v-if="byteLimitReached" :id="warningId" role="status" class="mt-1 text-center text-text-md text-gray-900">
      {{ byteLimitMessage }}
    </p>
  </div>
</template>
